/**
 * boost-commerce-api.js
 *
 * Boost Commerce Discovery & Suggest API helpers.
 *
 * RULES:
 *  - Never fires network requests automatically on page load.
 *  - All public functions live on window.theme.boostCommerceApi.
 *  - Credentials are read from window.BoostCommerceApiConfig or window.Shopify.
 */

(function () {

  // ─── Bootstrap ───────────────────────────────────────────────────────────

  var DEFAULT_API_BASE = 'https://services.mybcapps.com';

  var overrideCfg = window.BoostCommerceApiConfig || {};
  var DEFAULT_RECOMMENDATION_TYPE = String(overrideCfg.searchModalRecommendationType || 'bestsellers');
  var DEFAULT_MODEL_TYPE = String(overrideCfg.searchModalModelType || 'bestsellers');

  var API_BASE     = String(overrideCfg.apiBase  || DEFAULT_API_BASE).replace(/\/+$/, '');
  var SHOP_DOMAIN  = String(overrideCfg.shop     || (window.Shopify && window.Shopify.shop) || '');
  var API_TOKEN    = String(overrideCfg.token    || '');

  var ENDPOINTS = {
    recommend: API_BASE + '/discovery/recommend',
    suggest:   API_BASE + '/bc-sf-filter/search/suggest',
  };

  // ─── Shared helpers ───────────────────────────────────────────────────────

  /**
   * Returns the shopper's currently active currency (e.g. "USD"), preferring
   * the live window.Shopify.currency.active global over any static override,
   * since that global reflects the visitor's actual selected market/currency.
   * @returns {string}
   */
  function getActiveCurrency() {
    var shopify = typeof window !== 'undefined' ? window.Shopify : null;
    return (
      (shopify && shopify.currency && shopify.currency.active && String(shopify.currency.active).trim()) ||
      (overrideCfg.currency != null && String(overrideCfg.currency).trim()) ||
      ''
    );
  }

  /**
   * Builds request headers, optionally merging extra headers.
   * @param {Headers} [extra]
   * @returns {Headers}
   */
  function buildHeaders(extra) {
    var headers = new Headers();
    headers.append('Content-Type', 'application/json');
    if (API_TOKEN) headers.append('token', API_TOKEN);
    if (extra && typeof extra.forEach === 'function') {
      extra.forEach(function (value, key) { headers.set(key, value); });
    }
    return headers;
  }

  /**
   * Safely parses a fetch Response body as JSON.
   * Returns null instead of throwing on empty or malformed bodies.
   * @param {Response} response
   * @returns {Promise<object|null>}
   */
  function parseJsonSafely(response) {
    return response
      .text()
      .then(function (body) {
        if (!body) return null;
        try   { return JSON.parse(body); }
        catch { return null; }
      })
      .catch(function () { return null; });
  }

  // ─── Core API calls ───────────────────────────────────────────────────────

  /**
   * Calls the Boost /discovery/recommend endpoint.
   * Automatically injects the shop domain when not included in the payload.
   *
   * @param {object} payload   — Body sent to the endpoint.
   * @returns {Promise<{ ok: boolean, status: number|null, data: object|null, error?: string }>}
   */
  function recommend(payload) {
    var body = Object.assign({}, payload || {});
    if (!body.shop) body.shop = SHOP_DOMAIN;
    if (!body.currency) {
      var activeCurrency = getActiveCurrency();
      if (activeCurrency) body.currency = activeCurrency;
    }

    return fetch(ENDPOINTS.recommend, {
      method:  'POST',
      headers: buildHeaders(),
      body:    JSON.stringify(body),
    })
      .then(function (response) {
        return parseJsonSafely(response).then(function (data) {
          if (!response.ok) {
            return { ok: false, status: response.status, data: data, error: 'Boost recommend failed with status ' + response.status };
          }
          return { ok: true, status: response.status, data: data };
        });
      })
      .catch(function (err) {
        return { ok: false, status: null, data: null, error: (err && err.message) || 'Boost recommend network error' };
      });
  }

  /**
   * Calls the Boost /bc-sf-filter/search/suggest endpoint for
   * instant-search suggestions, products, and collections.
   *
   * @param {string} query                          — The search term.
   * @param {{ limit?: number, signal?: AbortSignal }} [options]
   * @returns {Promise<{ ok: boolean, status: number|null, data: object|null, error?: string }>}
   */
  function suggest(query, options) {
    var term = String(query || '').trim();

    // Edge case: empty query — return an empty result without making a network request.
    if (!term) {
      return Promise.resolve({ ok: true, status: 200, data: { all_empty: true, query: '' } });
    }

    var opt = options || {};
    var activeCurrency = getActiveCurrency();
    var url = ENDPOINTS.suggest +
      '?shop='  + encodeURIComponent(SHOP_DOMAIN) +
      '&q='     + encodeURIComponent(term) +
      (activeCurrency ? '&currency=' + encodeURIComponent(activeCurrency) : '') +
      (typeof opt.limit === 'number' ? '&limit=' + encodeURIComponent(String(opt.limit)) : '');

    return fetch(url, {
      method:      'GET',
      headers:     buildHeaders(),
      credentials: 'omit',
      signal:      opt.signal || null,
    })
      .then(function (response) {
        return parseJsonSafely(response).then(function (data) {
          if (!response.ok) {
            return { ok: false, status: response.status, data: data, error: 'Boost suggest failed with status ' + response.status };
          }
          return { ok: true, status: response.status, data: data };
        });
      })
      .catch(function (err) {
        // AbortError is expected when the user types fast — treat it as a clean cancel.
        if (err && err.name === 'AbortError') {
          return { ok: false, status: null, data: null, error: 'aborted' };
        }
        return { ok: false, status: null, data: null, error: (err && err.message) || 'Boost suggest network error' };
      });
  }

  // ─── Predictive-search HTML builder ──────────────────────────────────────

  /**
   * Escapes a string for safe insertion into HTML.
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#39;');
  }

  /**
   * Formats a numeric amount using a Shopify money format string.
   * Falls back to {{amount}} if no format is supplied.
   *
   * @param {number} amount
   * @param {string} [moneyFormat]
   * @returns {string}
   */
  function formatMoney(amount, moneyFormat) {
    var value = Number(amount);
    if (!Number.isFinite(value)) value = 0;
    var formatted = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return String(moneyFormat || '{{amount}}').replace('{{amount}}', formatted);
  }

  /**
   * Strips HTML tags and collapses whitespace from a string.
   * Used to generate plain-text product subtitles from body_html.
   *
   * @param {string} html
   * @returns {string}
   */
  function stripHtml(html) {
    return String(html || '')
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi,   '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Truncates a string to maxLen characters, appending '…' if cut.
   * @param {string} text
   * @param {number} [maxLen=80]
   * @returns {string}
   */
  function truncateText(text, maxLen) {
    var str = String(text || '');
    var limit = typeof maxLen === 'number' ? maxLen : 80;
    if (str.length <= limit) return str;
    return str.slice(0, Math.max(0, limit - 1)).trimEnd() + '…';
  }

  /**
   * Builds the predictive-search section HTML that the Shopify
   * PredictiveSearch web component expects, but populated with
   * Boost suggest data instead of the native Shopify results.
   *
   * @param {string} searchTerm      — The typed query.
   * @param {object} suggestPayload  — data from suggest().
   * @returns {string} Full section HTML string.
   */
  function buildPredictiveSearchHtml(searchTerm, suggestPayload) {
    var data = suggestPayload || {};

    // Cap results to keep the dropdown snappy.
    var products    = (Array.isArray(data.products)    ? data.products    : []).slice(0, 8);
    var collections = (Array.isArray(data.collections) ? data.collections : []).slice(0, 6);
    var suggestions = (
      Array.isArray(data.suggestions)  ? data.suggestions  :
      Array.isArray(data.did_you_mean) ? data.did_you_mean : []
    ).slice(0, 6);

    var productCount    = typeof data.total_product === 'number' ? data.total_product : products.length;
    var suggestionCount = suggestions.length;
    var collectionCount = collections.length;
    var totalCount      = productCount + suggestionCount + collectionCount;

    // Prefer the response's own money format, fall back to the theme global.
    var moneyFormat =
      (data.meta && (data.meta.money_format_with_currency || data.meta.money_format)) ||
      (window.theme && window.theme.money_format) ||
      '{{amount}}';

    var html = '';
    html += '<div class="wrapper">';
    html += '<span class="predictive-search-status visually-hidden" role="status" data-predictive-search-live-region-count-value>';
    html += escapeHtml(totalCount ? totalCount + ' results' : 'No results');
    html += '</span>';
    html += '<div class="search__results__wrapper" id="predictive-search-results" role="listbox" aria-label="' + escapeHtml('Search for ' + searchTerm) + '">';

    if (totalCount > 0) {
      html += '<div class="search__results__outer">';
      html += '<div class="search__results__other">';

      // ── Suggestions ──
      if (suggestionCount > 0) {
        html += '<div class="search__results__other__list">';
        html += '<div class="search__results__heading">';
        html += '<p class="search__results__title">Suggestions</p>';
        html += '<span class="badge">' + suggestionCount + '</span>';
        html += '</div>';
        html += '<ul aria-label="Suggestions" role="group">';

        suggestions.forEach(function (suggestion, index) {
          var suggestionUrl = '/search?q=' + encodeURIComponent(String(suggestion || '').trim());
          var animDelay     = (index + 1) * 90 + 10;
          html += '<li class="other__inline animates"';
          html += ' id="predictive-search-option-query-' + (index + 1) + '"';
          html += ' role="option" aria-selected="false"';
          html += ' style="animation-delay: ' + animDelay + 'ms;">';
          html += '<p class="other__inline__title">';
          html += '<a href="' + escapeHtml(suggestionUrl) + '">';
          html += '<span>' + escapeHtml(suggestion) + '</span>';
          html += '</a></p></li>';
        });

        html += '</ul></div>';
      }

      // ── Products ──
      if (products.length > 0) {
        html += '<div class="search__results__products">';
        html += '<div class="search__results__products__title">';
        html += '<div id="search_product_results_heading" class="search__results__heading">';
        html += '<p class="search__results__title">Products</p>';
        html += '<span class="badge">' + productCount + '</span>';
        html += '</div></div>';
        html += '<ul aria-label="Products" class="search__results__products__list">';

        products.forEach(function (product, index) {
          var handle = product && product.handle ? String(product.handle) : '';
          if (!handle) return; // Edge case: malformed product row

          var title      = (product && product.title) || '';
          var productUrl = '/products/' + encodeURIComponent(handle);
          var priceMin   = product && (product.price_min != null ? product.price_min : product.price);
          var compareAt  = product && product.compare_at_price_min != null ? product.compare_at_price_min : null;
          var isOnSale   = compareAt != null && Number(compareAt) > Number(priceMin);
          var isAvailable = !(product && product.available === false); // default to available
          var firstImage = (product && Array.isArray(product.images_info) && product.images_info[0]) || null;
          var subtitle   = truncateText(stripHtml(product && product.body_html), 80);
          var animDelay  = (suggestionCount + index + 1) * 90 + 10;

          var priceClass = 'price' + (isOnSale ? ' on-sale' : '') + (!isAvailable ? ' sold-out' : '');

          html += '<div class="product__inline animates"';
          html += ' style="animation-delay: ' + animDelay + 'ms;"';
          html += ' role="option" aria-selected="false">';
          html += '<a href="' + escapeHtml(productUrl) + '" class="product__inline__link">';

          // Product image — always render the container for layout consistency.
          var hasImage = !!(firstImage && firstImage.src);
          html += '<div role="presentation" class="product__inline__image' + (hasImage ? '' : ' product__inline__image--no-image') + '">';
          if (hasImage) {
            html += '<img class="img-aspect-ratio"';
            html += ' alt="'    + escapeHtml(title)                + '"';
            html += ' src="'    + escapeHtml(String(firstImage.src)) + '"';
            if (firstImage.width)  html += ' width="'  + String(firstImage.width)  + '"';
            if (firstImage.height) html += ' height="' + String(firstImage.height) + '"';
            html += '>';
          }
          html += '</div>';

          // Product text info
          html += '<div>';
          html += '<p class="product__inline__title">'  + escapeHtml(title)    + '</p>';
          if (subtitle) {
            html += '<p class="product__inline__subtitle">' + escapeHtml(subtitle) + '</p>';
          }
          html += '<p class="product__inline__price">';
          html += '<span class="' + escapeHtml(priceClass) + '">' + escapeHtml(formatMoney(priceMin, moneyFormat)) + '</span>';
          if (isOnSale) {
            html += '<span class="compare-at">' + escapeHtml(formatMoney(compareAt, moneyFormat)) + '</span>';
          }
          html += '</p>';
          html += '</div>';

          html += '</a></div>';
        });

        html += '</ul></div>';
      }

      // ── Collections ──
      if (collectionCount > 0) {
        html += '<div class="search__results__other__list">';
        html += '<div id="search_collection_results_heading" class="search__results__heading">';
        html += '<p class="search__results__title">Collections</p>';
        html += '<span class="badge">' + collectionCount + '</span>';
        html += '</div>';
        html += '<ul aria-label="Collections" role="group" class="search__results__collections__list">';

        collections.forEach(function (collection, index) {
          var handle = collection && collection.handle ? String(collection.handle) : '';
          if (!handle) return; // Edge case: malformed collection row

          var title         = (collection && collection.title) || handle;
          var collectionUrl = '/collections/' + encodeURIComponent(handle);
          var animDelay     = (suggestionCount + productCount + index + 1) * 90 + 10;

          html += '<div class="other__inline animates"';
          html += ' style="animation-delay: ' + animDelay + 'ms;"';
          html += ' id="predictive-search-option-collection-' + (index + 1) + '"';
          html += ' role="option" aria-selected="false">';
          html += '<p class="other__inline__title">';
          html += '<a href="' + escapeHtml(collectionUrl) + '">' + escapeHtml(title) + '</a>';
          html += '</p></div>';
        });

        html += '</ul></div>';
      }

      html += '</div></div>'; // .search__results__other + .search__results__outer

    } else {
      // ── No results ──
      html += '<div class="search__results__empty"><div aria-live="polite">';
      html += '<p>No results for <em>' + escapeHtml(searchTerm) + '</em></p>';
      html += '</div></div>';
    }

    // ── "Search for X" CTA ──
    html += '<div class="search__results__actions">';
    html += '<button class="btn btn--outline search__results__btn" tabindex="-1" role="option" aria-selected="false">';
    html += '<span data-predictive-search-search-for-text>' + escapeHtml('Search for ' + searchTerm) + '</span>';
    html += '</button>';
    html += '</div>';

    html += '</div></div>'; // #predictive-search-results + .wrapper

    return '<div id="shopify-section-predictive-search">' + html + '</div>';
  }

  // ─── Predictive-search fetch interceptor ─────────────────────────────────

  /**
   * Monkey-patches window.fetch to intercept Shopify's native predictive-search
   * section request and return Boost suggest results instead.
   *
   * This lets the existing PredictiveSearch web component work without modification.
   * Safe to call multiple times — installs the interceptor only once.
   */
  function installPredictiveSearchInterceptor() {
    if (typeof window.fetch !== 'function') return;
    if (window.__boostPredictiveInterceptorInstalled) return;
    window.__boostPredictiveInterceptorInstalled = true;

    var nativeFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      try {
        var url           = typeof input === 'string' ? input : (input && input.url) || '';
        var themeRoutes   = (window.theme && window.theme.routes) || {};
        var predictiveUrl = themeRoutes.predictive_search_url;

        // Only intercept the exact Shopify predictive-search section endpoint.
        if (predictiveUrl && url && String(url).indexOf(String(predictiveUrl)) === 0) {
          var parsedUrl = new URL(url, window.location.origin);

          if (parsedUrl.searchParams.get('section_id') === 'predictive-search') {
            var query  = parsedUrl.searchParams.get('q') || '';
            var signal = init && init.signal;

            return suggest(query, { signal: signal }).then(function (result) {
              var payload = result && result.ok ? result.data : null;
              var body    = buildPredictiveSearchHtml(String(query).trim(), payload || {});
              return new Response(body, {
                status:  200,
                headers: { 'Content-Type': 'text/html; charset=utf-8' },
              });
            });
          }
        }
      } catch (e) {
        // Any error in interception logic → fall through to native fetch.
      }

      return nativeFetch(input, init);
    };
  }

  // ─── Convenience helpers ──────────────────────────────────────────────────

  /**
   * Shorthand for bestseller recommendations used by the search modal.
   * Any field can be overridden via the second argument.
   *
   * @param {number} [limit=6]
   * @param {object} [overrides]
   * @returns {Promise<{ ok: boolean, status: number|null, data: object|null, error?: string }>}
   */
  function fetchPopularProducts(limit, overrides) {
    var payload = Object.assign({
      shop:               SHOP_DOMAIN,
      recommendationType: DEFAULT_RECOMMENDATION_TYPE,
      limit:              typeof limit === 'number' ? limit : 6,
      modelType:          DEFAULT_MODEL_TYPE,
      wid:                'search-modal-popular',
      event_type:         'init',
    }, overrides || {});

    return recommend(payload);
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  window.theme = window.theme || {};
  window.theme.boostCommerceApi = {
    endpoints:                        ENDPOINTS,
    recommend:                        recommend,
    suggest:                          suggest,
    popular:                          fetchPopularProducts,
    fetchPopularProducts:             fetchPopularProducts,
    installPredictiveSearchInterceptor: installPredictiveSearchInterceptor,
  };

})();